import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getCarlinkLot, getCarlinkProtocolUrl } from '@/api/carlink'
import { Button } from '@/components/ui/Button'
import { formatMoscowDateTime } from '@/utils/format'

const KNOWN_STATUSES = ['DRAFT', 'MODERATED', 'ACTIVE', 'FINISHED', 'SUCCESS', 'BUYNOW', 'FAILED']

/**
 * «Торги Carlink» в карточке лида СберАвто: лот, статус, время, протокол.
 * Данные — carlink_lots по lead_id (мост находит лот по отчёту осмотра заказа лида).
 * Данные шлёт мост Carlink (edge carlink-sync → carlink_lots). Пока лота нет —
 * блок не показываем.
 */
export function CarlinkAuctionCard({ leadId, className }: { leadId: string; className: string }) {
  const { t } = useTranslation()
  const { data: lot } = useQuery({
    queryKey: ['carlink-lot', leadId],
    queryFn: () => getCarlinkLot(leadId),
    refetchInterval: 60_000,
  })
  const protocolMut = useMutation({
    mutationFn: (path: string) => getCarlinkProtocolUrl(path),
    onSuccess: (url) => window.open(url, '_blank', 'noopener'),
  })
  if (!lot) return null

  const status = lot.status && KNOWN_STATUSES.includes(lot.status)
    ? t(`carlink.status.${lot.status}`)
    : (lot.status ?? '—')
  const row = (label: string, value: string | null) =>
    value ? (
      <div className="flex gap-2">
        <dt className="text-[color:var(--stryzh-color-text-secondary)]">{label}</dt>
        <dd className="text-[color:var(--stryzh-color-text-primary)]">{value}</dd>
      </div>
    ) : null

  return (
    <section className={className}>
      <h2 className="stryzh-heading-card mb-4">{t('carlink.title')}</h2>
      <dl className="grid gap-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)]">
        {row(t('carlink.statusLabel'), status)}
        {row(t('carlink.lot'), lot.id_auction ? `№ ${lot.id_auction}` : null)}
        {row(t('carlink.start'), lot.dt_start ? formatMoscowDateTime(lot.dt_start) : null)}
        {row(t('carlink.end'), lot.dt_end ? formatMoscowDateTime(lot.dt_end) : null)}
        {row(t('carlink.finished'), lot.finished_at ? formatMoscowDateTime(lot.finished_at) : null)}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {lot.lot_url ? (
          <a
            className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] font-medium text-[color:var(--stryzh-color-accent-orange)] hover:underline"
            href={lot.lot_url}
            target="_blank"
            rel="noreferrer"
          >
            {t('carlink.openLot')}
          </a>
        ) : null}
        {lot.protocol_path ? (
          <Button
            type="button"
            variant="secondary"
            disabled={protocolMut.isPending}
            onClick={() => protocolMut.mutate(lot.protocol_path!)}
          >
            {t('carlink.protocol')}
          </Button>
        ) : null}
        {protocolMut.isError ? (
          <span className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-semantic-error)]">
            {t('carlink.protocolError')}
          </span>
        ) : null}
      </div>
    </section>
  )
}
